<template>
  <CustomRender />
</template>

<script lang="ts" setup name="SvgIcon">
import { compile, computed, h, ref, watch } from 'vue';
import { omit, toNumber } from 'lodash-es';
import svgCacheManage from './svgCacheManage';

type Props = {
  iconName: string;
  width?: number | string;
  height?: number | string;
  currentColor?: boolean;
  currentColorStroke?: boolean;
  color?: string;
};

const props = withDefaults(defineProps<Props>(), {
  currentColor: false, // 默认不替换为currentColor
});

/**
 * 加载svg
 * @param url
 * @param callback
 * @param errorCallback
 */
const loadSvg = (url: string, callback: Function, errorCallback: Function) => {
  if (url) {
    // get svg cache
    const svgCache = svgCacheManage.getSvgCache(url);
    if (!!svgCache) {
      callback(svgCache.svgXml, svgCache.svgString);
      return;
    }

    var req = new XMLHttpRequest();
    req.onreadystatechange = function () {
      if (req.readyState == 4) {
        // readyState is DONE
        var status = req.status;
        if (status == 200) {
          // setting up caching
          svgCacheManage.setSvgCache(url, req.responseXML, req.responseText.trim());

          // request status is OK
          callback(req.responseXML, req.responseText.trim());
        } else if (status >= 400) {
          // request status is error (4xx or 5xx)
          errorCallback();
        } else if (status == 0) {
          // request status 0 can indicate a failed cross-domain call
          errorCallback();
        }
      }
    };
    req.open('GET', url, true);
    req.send();
  } else {
    svgHtml.value = '';
  }
};

const svgHtml = ref('');

const computedUrl = computed(() => {
  const svgFileName: string = window.SVG_CONFIG[props.iconName] ?? '';
  if (!svgFileName) {
    return '';
  }
  return new URL(`../../assets/svgs/${svgFileName}`, import.meta.url).href;
});

const initLoadSvg = () => {
  if (!computedUrl.value) {
    svgHtml.value = '';
    return;
  }

  loadSvg(
    computedUrl.value,
    (_svgXml: any, svgString: string) => {
      if (svgString) {
        let content = svgString;
        if (props.currentColorStroke) {
          content = svgString.replace(/stroke="[a-zA-Z#0-9]*"/, 'stroke="currentColor"');
        }
        if (props.currentColor) {
          content = content.replace(/fill="[a-zA-Z#0-9]*"/g, 'fill="currentColor"');
        }

        // const regStr = svgString.match(/<svg((?!>)).*>/)?.[0] ?? '';
        // const width = (regStr.match(/width="[a-zA-Z#0-9]*"/)?.[0] ?? '').replace('width=', '').replace(/"/g, '');
        // const height = (regStr.match(/height="[a-zA-Z#0-9]*"/)?.[0] ?? '').replace('height=', '').replace(/"/g, '');
        const width = toNumber(_svgXml.querySelector('svg').getAttribute('width'));
        const height = toNumber(_svgXml.querySelector('svg').getAttribute('height'));

        if (width && height) {
          // const realHeight = props.height ? props.height : (+props.width / +width) * +height;
          // const vWidth = ((+props.width * 100) / 1125).toFixed(6);
          // const vHeight = ((+realHeight * 100) / 1125).toFixed(6);
          // const mWidth = (+props.width / 2).toFixed(6);
          // const mheight = (+realHeight / 2).toFixed(6);

          // content = content.replace(`width="${width}`, `width="min(${parseFloat(vWidth)}vw, ${parseFloat(mWidth)}px)`);
          // content = content.replace(`height="${height}`, `height="min(${parseFloat(vHeight)}vw, ${parseFloat(mheight)}px)`);

          if (toNumber(props.width) || toNumber(props.height)) {
            const rate = width / height;
            const _width = props.width ? toNumber(props.width) : toNumber(props.height) * rate;
            const _height = props.height ? toNumber(props.height) : toNumber(props.width) / rate;
            const vWidth = ((_width * 100) / 1125).toFixed(6);
            const vHeight = ((_height * 100) / 1125).toFixed(6);
            const mWidth = (_width / 2).toFixed(6);
            const mHeight = (_height / 2).toFixed(6);
            content = content.replace(
              `width="${width}`,
              `width="min(${parseFloat(vWidth)}vw, ${parseFloat(mWidth)}px)`
            );
            content = content.replace(
              `height="${height}`,
              `height="min(${parseFloat(vHeight)}vw, ${parseFloat(mHeight)}px)`
            );
          } else {
            content = content.replace(`width="${width}`, `width="1em`);
            content = content.replace(`height="${height}`, `height="${(height / width).toFixed(4)}em`);
          }
        }
        svgHtml.value = content;
      }
    },
    () => {
      console.error('SVG load failed');
    }
  );
};

watch(
  () => props.iconName,
  () => {
    initLoadSvg();
  },
  {
    immediate: true,
    deep: true,
  }
);

/**
 * 自定义render
 */
const CustomRender = () => {
  return h(compile(svgHtml.value), {
    ...omit(props, ['width', 'height', 'iconName', 'currentColor', 'currentColorStroke']),
    class: 'svg-icon',
    style: `color: ${props.color}`,
  });
};
</script>

<style scoped>
.svg-icon {
  fill: currentcolor;
  vertical-align: middle;
  cursor: pointer;
  color: var(--theme-icon-default-color);
  font-size: 48px;
}
</style>
